import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import { hoverable, theme } from '@/shared/design-system';
import { forMobile } from '@/styles/responsive';

export type FundCardChipTone = 'positive' | 'negative' | 'neutral';

const chipToneStyles: Record<
  FundCardChipTone,
  { background: string; value: string; label: string }
> = {
  positive: {
    background: theme.colors.brand.subtle,
    value: theme.colors.brand.text,
    label: theme.colors.brand.textSubtle,
  },
  negative: {
    background: theme.colors.error.subtle,
    value: theme.colors.error.text,
    label: alpha(theme.colors.error.text, 0.7),
  },
  neutral: {
    background: theme.colors.info.subtleAlt,
    value: theme.colors.info.solid,
    label: theme.colors.info.textSubtle,
  },
};

export const getChipToneStyles = (tone: FundCardChipTone) => chipToneStyles[tone];

export const FundCardWrapper = styled(Link)(({ theme: muiTheme }) => ({
  ...hoverable(theme, ['surfaceRaised', 'elevate']),
  width: '272px',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.colors.background.raised,
  borderRadius: theme.radius[16],
  overflow: 'hidden',
  textDecoration: 'none',

  [forMobile(muiTheme)]: {
    width: '240px',
  },
}));

export const FundCardSkeletonWrapper = styled('div')(({ theme: muiTheme }) => ({
  width: '272px',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.colors.background.raised,
  borderRadius: theme.radius[16],
  overflow: 'hidden',

  [forMobile(muiTheme)]: {
    width: '240px',
  },
}));

export const FundCardSkeletonChipSlot = styled('div')({
  flex: 1,
  minWidth: 0,
});

export const FundCardLogoWrapper = styled('div')({
  padding: theme.spacing[4],
  paddingBottom: theme.spacing[0],
});

export const FundCardLogo = styled('img')({
  width: '100%',
  aspectRatio: '280 / 156',
  objectFit: 'cover',
  display: 'block',
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.overlay,
});

export const FundCardLogoFallback = styled('div')({
  width: '100%',
  aspectRatio: '280 / 156',
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.overlay,
});

export const FundCardContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[8],
  padding: `${theme.spacing[8]}px ${theme.spacing[12]}px ${theme.spacing[12]}px`,
  minWidth: 0,
});

export const FundCardChipsRow = styled('div')({
  display: 'flex',
  gap: theme.spacing[4],
});

export const FundCardChip = styled('div', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: FundCardChipTone }>(({ tone }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing[8],
  borderRadius: theme.radius[12],
  backgroundColor: chipToneStyles[tone].background,

  'svg path': {
    fill: chipToneStyles[tone].value,
  },
}));

export const FundCardChipIcon = styled('div')(({ theme: muiTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 28,

  [forMobile(muiTheme)]: {
    height: 24,
  },
}));

export const FundCardMinInvestRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  minWidth: 0,
});
